import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react'

import { IconNames } from '@sportsyou/react-icons'
import { Image, Upload, VideoClip, VideoPlaylist } from '@sportsyou/api'
import { getTranscodeUrl, isImage, isVideo } from '@sportsyou/core'

export interface ExtendedVideoPlaylist extends VideoPlaylist {
  postId?: string
}

export interface LightboxItemMenuItem {
  title: string
  onClick: () => void
  icon?: IconNames
}

export interface LightboxItemProps {
  active?: boolean
  autoPlay?: boolean
  canDelete?: boolean
  clip?: VideoClip
  menuItems?: LightboxItemMenuItem[]
  type?: string

  // Props equivalent to an Upload object, available if needed
  clipId?: string
  contentType?: string
  createdAt?: string
  createdById?: string
  deletedAt?: string
  durationInMs?: number
  fileName?: string
  height?: number
  id?: string
  isAnimated?: boolean
  isTeamMedia?: boolean
  poster?: Image
  postId?: string
  postIds?: string[]
  processedAt?: string
  profileType?: string
  signedUrl?: string
  teamId?: string
  teamName?: string
  transcodes?: Image[]
  videoTeamId?: string
  viewUrl?: string
  width?: number
  // sponsorInfo?: UploadSponsorInfo
  // zipProgress?: ZipProgress
}

export interface LightboxShowWithItemsProps {
  items: LightboxItemProps[]
  index?: number
  playlist?: ExtendedVideoPlaylist | undefined
}

interface LightboxState {
  canDisplayOnBrowser: (contentType?: string) => boolean
  clearLightboxItems: () => void
  getViewableDataFromUploadForLightbox: (item: Upload) => {
    contentType?: string
    type?: string
    viewUrl?: string
  }
  hideLightbox: () => void
  index?: number
  isOpen?: boolean
  items: LightboxItemProps[]
  playlist?: ExtendedVideoPlaylist
  setLightboxIndex: (index: number) => void
  setLightboxItems: (items: LightboxItemProps[]) => void
  showLightbox: () => void
  showLightboxWithItems: (props: LightboxShowWithItemsProps) => void
}

interface LightboxProviderProps {
  children: ReactNode
}

const LightboxContext = createContext<LightboxState | undefined>(undefined)

export function LightboxProvider({ children }: LightboxProviderProps) {
  const [index, setLightboxIndex] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [items, setLightboxItems] = useState<LightboxItemProps[]>([])
  const [playlist, setPlaylist] = useState<ExtendedVideoPlaylist | undefined>()

  const showLightbox = useCallback(() => setIsOpen(true), [])

  const hideLightbox = useCallback(() => {
    setIsOpen(false)
    setLightboxIndex(0)
    setLightboxItems([])
    setPlaylist(undefined)
  }, [])

  const showLightboxWithItems = useCallback(
    ({ index, items, playlist }: LightboxShowWithItemsProps) => {
      setLightboxItems(items)
      setLightboxIndex(index || 0)
      setPlaylist(playlist)
      setIsOpen(true)
    },
    []
  )

  const clearLightboxItems = useCallback(() => {
    setLightboxItems([])
  }, [])

  const browserDisplayableImageTypes = ['jpeg', 'png', 'gif', 'webp']
  const browserDisplayableVideoTypes = [
    'mp4',
    'webm',
    'ogg',
    'm3u8',
    'quicktime',
  ]

  const canDisplayOnBrowser = (contentType?: string): boolean => {
    const imageType = contentType?.split('/').pop() ?? ''
    return browserDisplayableImageTypes.includes(imageType)
  }

  const canPlayVideoInBrowser = (contentType?: string): boolean => {
    const videoType = contentType?.split('/').pop() ?? ''
    return browserDisplayableVideoTypes.includes(videoType)
  }

  const getViewableDataFromUploadForLightbox = (item?: Upload) => {
    const viewableData = {
      contentType: item?.contentType ?? undefined,
      viewUrl: item?.viewUrl ?? undefined,
      type: 'file',
    }
    const contentType = item?.contentType ?? ''
    if (isImage(contentType)) {
      if (canDisplayOnBrowser(contentType)) {
        viewableData.type = 'image'
      } else {
        // Find a transcode that can be displayed in the browser
        viewableData.viewUrl = getTranscodeUrl({
          upload: item?.transcodes as Image[],
          transcodeTypes: ['feedLarge', 'feed', 'media'],
        })
        viewableData.type = 'image'
      }
    } else if (isVideo(contentType)) {
      const transcodes = item?.transcodes as Image[]
      const transcodeTypes = ['HLS', 'VIDEO']
      const videoViewUrl =
        transcodes && getTranscodeUrl({ upload: transcodes, transcodeTypes })
      const videoContentType =
        transcodes
          ?.filter((tr) => transcodeTypes.includes(tr.transcodeType as string))
          .shift()?.contentType ?? undefined
      if (canPlayVideoInBrowser(contentType)) {
        viewableData.type = 'video'
        viewableData.viewUrl = videoViewUrl
        viewableData.contentType = videoContentType
      }
    }
    return viewableData
  }

  return (
    <LightboxContext.Provider
      value={{
        canDisplayOnBrowser,
        clearLightboxItems,
        getViewableDataFromUploadForLightbox,
        hideLightbox,
        index,
        isOpen,
        items,
        playlist,
        setLightboxIndex,
        setLightboxItems,
        showLightbox,
        showLightboxWithItems,
      }}
    >
      {children}
    </LightboxContext.Provider>
  )
}

export function useLightbox() {
  const context = useContext(LightboxContext)
  if (!context) {
    throw new Error('useLightbox must be used within a LightboxProvider')
  }
  return context
}
